import { isFeedReader } from '../../pages/feed/feedDetailModel';
import { isNoticeReader } from '../../features/articles/readerRoutes';
import { isSponsorshipPath } from '../../features/sponsorship/model';
import { isLegalDocumentPath } from '../../features/legal/routes';
import { useSignupDraft, removeLegacyDraft } from '../../features/signup/draft';
import {
  isPublicProfileSummaryPath,
  isPublicProfileRecordsPath,
  isPublicProfileTastePath,
  isPublicProfileBadgesPath,
} from '../../features/profile/routes';
import { isMemberReviewSurface } from '../../utils/reviewRoutePaths';
import { isAlbumDetailRoute } from '../../pages/review/components/album/albumHeaderModel';
import { SearchViewMemoryProvider } from '../../features/search/SearchViewMemoryContext';
import { useSearchScrollMemory } from '../../features/search/useSearchScrollMemory';
import { isSearchScrollRoute } from '../../features/search/searchViewMemory';
import {
  SearchEditingProvider,
  useSearchEditing,
} from '../../features/search/SearchEditingContext';
import { browseChrome } from '../../features/browse/chrome';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AnalyticsChoice from '../common/AnalyticsChoice';
import { Box } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import MobilePrimaryNavigation, { showPrimaryNavigation } from './MobilePrimaryNavigation';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import Footer from './Footer';
import { WebPreviewPlayer, useWebPreview } from '../../features/preview/PreviewControls';
import { useUserStore } from '../../stores/userStore';
import { webPreviewPlayer } from '../../features/preview/previewPlayer';

import '../../styles/Layout.css';
import BadgeAchievementLifecycle from '../../features/badges/BadgeAchievementLifecycle';
import { useProfileScrollMemory } from '../../features/profile/useProfileScrollMemory';

const Layout = () => {
  const location = useLocation();
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const analyticsTrigger = useRef(null);
  useEffect(() => {
    removeLegacyDraft();
    if (!/^\/signup(?:\/detail)?\/?$/.test(location.pathname)) useSignupDraft.getState().clear();
  }, [location.pathname]);
  useEffect(
    () =>
      useUserStore.subscribe((a, b) => {
        if (a.user !== b.user || a.isLogin !== b.isLogin) useSignupDraft.getState().clear();
      }),
    [],
  );
  const isAdmin = useUserStore((state) => state.isAdmin);
  const settingsStack =
    /^\/(?:login(?:\/(?:success|error|apple\/return))?|signup(?:\/detail)?)\/?$/.test(
      location.pathname,
    ) ||
    /^\/password-(change|forgot|reset)\/?$/.test(location.pathname) ||
    isNoticeReader(location.pathname) ||
    /^\/explore\/timeline\/?$/.test(location.pathname) ||
    location.pathname === '/feed' ||
    location.pathname === '/feed/' ||
    isFeedReader(location.pathname) ||
    isSponsorshipPath(location.pathname) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      isLegalDocumentPath(location.pathname)) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      /^\/contact\/?$/.test(location.pathname)) ||
    /^\/mypage\/(settings|blocked|push-settings|find-members|visibility|edit)\/?$/.test(
      location.pathname,
    ) ||
    /^\/notifications\/?$/.test(location.pathname);
  const editing = useSearchEditing();
  useSearchScrollMemory(Boolean(editing?.active));
  const profileScroll = useProfileScrollMemory();
  const preview = useWebPreview();
  const myPage =
    /^\/(?:login(?:\/(?:success|error|apple\/return))?|signup(?:\/detail)?)\/?$/.test(
      location.pathname,
    ) ||
    /^\/password-(change|forgot|reset)\/?$/.test(location.pathname) ||
    isNoticeReader(location.pathname) ||
    /^\/explore\/timeline\/?$/.test(location.pathname) ||
    location.pathname === '/feed' ||
    location.pathname === '/feed/' ||
    isFeedReader(location.pathname) ||
    isSponsorshipPath(location.pathname) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      isLegalDocumentPath(location.pathname)) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      /^\/contact\/?$/.test(location.pathname)) ||
    /^\/notifications\/?$/.test(location.pathname) ||
    /^\/mypage(?:\/[^/]+)?\/?$/.test(location.pathname) ||
    isPublicProfileSummaryPath(location.pathname) ||
    isPublicProfileRecordsPath(location.pathname) ||
    isPublicProfileTastePath(location.pathname) ||
    isPublicProfileBadgesPath(location.pathname);
  const browsePage = Boolean(browseChrome(location.pathname));
  const artistPage = browseChrome(location.pathname)?.detail === 'artist';
  const playlistPage = browseChrome(location.pathname)?.detail === 'playlist';
  const userId = useUserStore((state) => state.user?.id);
  useEffect(() => () => webPreviewPlayer.dismiss(), [userId]);
  const { t, locale } = useWebLocale();

  // Restore position before paint so a new route never displays the previous scroll offset.
  useLayoutEffect(() => {
    if (!isSearchScrollRoute(location.pathname) && !profileScroll) window.scrollTo(0, 0);
  }, [location.pathname, profileScroll]);

  return (
    <Box
      className={[
        !settingsStack && showPrimaryNavigation(location.pathname) ? 'pp-primary-layout' : '',
        location.pathname === '/'
          ? 'pp-home-layout'
          : location.pathname === '/explore'
            ? 'pp-explore-layout'
            : browsePage
              ? 'pp-browse-layout'
              : '',
        ['/explore', '/albums', '/reviews', '/lists'].includes(location.pathname)
          ? 'pp-explore-navigation-layout'
          : '',
        location.pathname.replace(/\/$/, '') === '/search'
          ? `pp-search-home-layout${editing?.active ? ' pp-search-home-active' : ''}`
          : '',
        isAlbumDetailRoute(location.pathname) ? 'pp-album-route' : '',
        myPage ? 'pp-my-route' : '',
        artistPage ? 'pp-artist-route' : '',
        playlistPage ? 'pp-playlist-route' : '',
        isMemberReviewSurface(location.pathname) ? 'pp-member-review-route' : '',
        preview.track ? 'pp-has-preview' : '',
      ].join(' ')}
      sx={{
        minHeight: '100svh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: myPage
          ? 'var(--my-global-height, 0px)'
          : isMemberReviewSurface(location.pathname)
            ? 'var(--member-header-height, 0px)'
            : playlistPage
              ? 'var(--playlist-global-height, 0px)'
              : artistPage
                ? 'var(--artist-global-height, 0px)'
                : isAlbumDetailRoute(location.pathname)
                  ? '56px'
                  : location.pathname === '/' || location.pathname === '/explore' || browsePage
                    ? 'calc(var(--home-header-height, 56px) + var(--explore-nav-height, 0px))'
                    : { xs: '56px', md: '60px' }, // Header 높이만큼 상단 여백

        boxSizing: 'border-box',
      }}
    >
      <a className="pp-skip-link" href="#pp-content">
        {t('skip')}
      </a>
      {/* 헤더 */}
      <Header />
      <BadgeAchievementLifecycle />

      {/* 라우팅된 페이지 */}
      <Box
        id="pp-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: '100%',
          minHeight: {
            xs: 'calc(100svh - 56px)',
            md: 'calc(100svh - 110px)',
            lg: 'calc(100svh - 120px)',
          },
        }}
      >
        <Outlet />
      </Box>

      {/* 푸터 */}
      {!settingsStack && !(/^\/mypage(?:\/[^/]+)?\/?$/.test(location.pathname) && !userId) && (
        <Footer
          analyticsTrigger={analyticsTrigger}
          onAnalyticsOpen={() => setAnalyticsOpen(true)}
        />
      )}
      {!settingsStack && <MobilePrimaryNavigation />}
      <WebPreviewPlayer />
      <AnalyticsChoice
        open={analyticsOpen}
        locale={locale}
        onClose={() => {
          setAnalyticsOpen(false);
          if (analyticsOpen) analyticsTrigger.current?.focus();
        }}
      />
    </Box>
  );
};

export default function SearchAwareLayout() {
  return (
    <SearchViewMemoryProvider>
      <SearchEditingProvider>
        <Layout />
      </SearchEditingProvider>
    </SearchViewMemoryProvider>
  );
}
